import React, { useState, useEffect } from 'react';
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlineExclamationCircle,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXMark
} from 'react-icons/hi2';
import { authService } from '../../services';
import masterDataService from '../../services/masterDataService';
import SharedModal from '../../components/common/SharedModal';
import PageLoading from '../../components/common/PageLoading';

const FailureModes = () => {
  const [failureModes, setFailureModes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingFailureMode, setEditingFailureMode] = useState(null);
  const [formData, setFormData] = useState({
    failureModeCode: '',
    failureModeName: '',
    description: '',
    status: 'Active',
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const tenantId = authService.getTenantId();

  useEffect(() => {
    if (tenantId) {
      loadFailureModes();
    } else {
      setError('Please login to view failure modes');
      setLoading(false);
    }
  }, [tenantId]);

  const loadFailureModes = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await masterDataService.getFailureModes(tenantId);
      setFailureModes(data);
    } catch (err) {
      setError('Failed to load failure modes. Please try again.');
      console.error('Error loading failure modes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredFailureModes = failureModes.filter((fm) =>
    fm.failureModeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    fm.failureModeCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    fm.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => {
    setEditingFailureMode(null);
    setFormData({
      failureModeCode: '',
      failureModeName: '',
      description: '',
      status: 'Active',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEdit = (failureMode) => {
    setEditingFailureMode(failureMode);
    setFormData({
      failureModeCode: failureMode.failureModeCode || '',
      failureModeName: failureMode.failureModeName || '',
      description: failureMode.description || '',
      status: failureMode.status || 'Active',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      setError('');
      await masterDataService.deleteFailureMode(id);
      await loadFailureModes();
      setDeleteConfirm(null);
    } catch (err) {
      setError('Failed to delete failure mode. Please try again.');
      console.error('Error deleting failure mode:', err);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.failureModeCode.trim()) {
      errors.failureModeCode = 'Failure mode code is required';
    } else if (formData.failureModeCode.length > 50) {
      errors.failureModeCode = 'Failure mode code must be less than 50 characters';
    }
    if (!formData.failureModeName.trim()) {
      errors.failureModeName = 'Failure mode name is required';
    } else if (formData.failureModeName.length > 100) {
      errors.failureModeName = 'Failure mode name must be less than 100 characters';
    }
    if (formData.description && formData.description.length > 500) {
      errors.description = 'Description must be less than 500 characters';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const failureModeData = {
        tenantId: tenantId,
        failureModeCode: formData.failureModeCode.trim(),
        failureModeName: formData.failureModeName.trim(),
        description: formData.description.trim() || null,
        status: formData.status,
      };

      if (editingFailureMode) {
        failureModeData.failureModeId = editingFailureMode.failureModeId;
        await masterDataService.updateFailureMode(editingFailureMode.failureModeId, failureModeData);
      } else {
        await masterDataService.createFailureMode(failureModeData);
      }

      await loadFailureModes();
      setShowModal(false);
      setFormData({ failureModeCode: '', failureModeName: '', description: '', status: 'Active' });
      setEditingFailureMode(null);
    } catch (err) {
      const errorMessage = err.response?.data?.message ||
        err.response?.data?.errors ||
        err.message ||
        'Failed to save failure mode. Please try again.';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingFailureMode(null);
    setFormData({ failureModeCode: '', failureModeName: '', description: '', status: 'Active' });
    setFormErrors({});
  };

  if (loading) {
    return <PageLoading message="Loading failure modes..." />;
  }

  return (
    <div className="pagecontainer  failure-modes-page">
      <div className="page-header">
        <div className="header-content">
          <h1>Failure Modes</h1>
          <p>Manage failure modes for equipment breakdowns and maintenance analysis</p>
        </div>
        <div className='header-actions'>
          <button className="btn-primary" onClick={handleAdd}>
            <HiOutlinePlus />
            Add Failure Mode
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container pad10 kizantable">
        <div className="table-header">
          <div className='left-group'>
            <div className="search-box">
              <HiOutlineMagnifyingGlass className="search-icon" />
              <input
                type="text"
                placeholder="Search failure modes..."
                value={searchTerm}
                onChange={handleSearch}
                className="search-input"
              />
            </div>
            <div className="table-info">
              {filteredFailureModes.length} {filteredFailureModes.length === 1 ? 'failure mode' : 'failure modes'}
            </div>
          </div>
        </div>

        {filteredFailureModes.length === 0 ? (
          <div className="empty-state">
            <HiOutlineExclamationCircle className="empty-icon" />
            <h3>No failure modes found</h3>
            <p>
              {searchTerm
                ? 'Try adjusting your search terms'
                : 'Get started by creating your first failure mode'}
            </p>
            {!searchTerm && (
              <button className="btn-primary" onClick={handleAdd}>
                <HiOutlinePlus className="btn-icon" />
                Add Failure Mode
              </button>
            )}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Code</th>
                <th>Name</th>
                <th>Description</th>
                <th>Status</th>
                <th>Created</th>
                <th className='text-center'>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredFailureModes.map((fm, index) => (
                <tr key={fm.failureModeId}>
                  <td>{index + 1}</td>
                  <td className="code-cell">{fm.failureModeCode}</td>
                  <td className="name-cell">{fm.failureModeName}</td>
                  <td className="description-cell">{fm.description || '-'}</td>
                  <td>
                    <span className={`status-badge ${fm.status?.toLowerCase()}`}>
                      {fm.status || 'Active'}
                    </span>
                  </td>
                  <td>
                    {fm.createdAt
                      ? new Date(fm.createdAt).toLocaleDateString()
                      : '-'}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon-edit"
                        onClick={() => handleEdit(fm)}
                        title="Edit"
                      >
                        <HiOutlinePencil />
                      </button>
                      <button
                        className="btn-icon-delete"
                        onClick={() => setDeleteConfirm(fm)}
                        title="Delete"
                      >
                        <HiOutlineTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create/Edit Modal */}
      <SharedModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingFailureMode ? 'Edit Failure Mode' : 'New Failure Mode'}
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleCloseModal}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="failure-mode-form"
              className="btn-primary"
              disabled={submitting}
            >
              {submitting
                ? editingFailureMode
                  ? 'Updating...'
                  : 'Creating...'
                : editingFailureMode
                ? 'Update'
                : 'Create'}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <form id="failure-mode-form" onSubmit={handleSubmit} className="modal-form">
            <div className="form-group">
              <label htmlFor="failureModeCode">
                Failure Mode Code <span className="required">*</span>
              </label>
              <input
                type="text"
                id="failureModeCode"
                value={formData.failureModeCode}
                onChange={(e) =>
                  setFormData({ ...formData, failureModeCode: e.target.value.toUpperCase() })
                }
                className={formErrors.failureModeCode ? 'error' : ''}
                placeholder="e.g., BEARING_FAIL, SEAL_LEAK, MOTOR_OVERHEAT"
                maxLength={50}
              />
              {formErrors.failureModeCode && (
                <span className="field-error">{formErrors.failureModeCode}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="failureModeName">
                Failure Mode Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="failureModeName"
                value={formData.failureModeName}
                onChange={(e) =>
                  setFormData({ ...formData, failureModeName: e.target.value })
                }
                className={formErrors.failureModeName ? 'error' : ''}
                placeholder="e.g., Bearing Failure, Seal Leakage, Motor Overheating"
                maxLength={100}
              />
              {formErrors.failureModeName && (
                <span className="field-error">{formErrors.failureModeName}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                className={formErrors.description ? 'error' : ''}
                placeholder="Enter description (optional)"
                rows={3}
                maxLength={500}
              />
              {formErrors.description && (
                <span className="field-error">{formErrors.description}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                value={formData.status}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value })
                }
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>

          </form>
        </div>
      </SharedModal>

      {/* Delete Confirmation Modal */}
      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirm Delete"
        customClass="small"
        footer={
          <>
            <button
              type="button"
              className="btn-secondary btn"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-danger btn"
              onClick={() => handleDelete(deleteConfirm.failureModeId)}
            >
              Delete
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <p>
            Are you sure you want to delete{' '}
            <strong>{deleteConfirm?.failureModeName}</strong>?
          </p>
          <p className="warning-text">
            This action cannot be undone. The failure mode will be marked as inactive.
          </p>
        </div>
      </SharedModal>
    </div>
  );
};

export default FailureModes;

